import { ReactComponent as ShowClaim } from 'assett/icons/show_password.svg';
import SelectGeneric from 'components/commons/form/SelectGeneric';
import dayjs from 'dayjs';
import { getClaimDetails } from 'feature/claims/claimSlice';
import {
	getComplaintTrackingDetail,
	resetComplaintDetail,
	saveComplaint,
	setComplaintValue,
} from 'feature/complaintTracking/complaintTrackingSlice';
import { useComplaintHook } from 'hooks/complaint.hook';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { AppDispatch, RootState } from 'store/store';
import { Button, DatePicker, Stack, TextField, Typography } from 'styles';
import { theme } from 'styles/theme/ThemeStyle';
import { IComplaintTrackingDetail } from 'types/ComplaintTracking';
import { STATE_KEYS } from 'types/Store';
import { COMPLAINT_NOTE_MAX_LENGTH, TRANSLATION_KEYS } from 'utils/constants';
import { useTranslation } from 'utils/translations';
import { formatDayjsForBE, getShortDate } from 'utils/utils';
import UploadGrid from '../UploadFile/UploadGrid/UploadGrid';
import ComplaintAttachmentsDetails from './ComplaintAttachmentsDetails';
import ComplaintDetailsButtons from './components/ComplaintDetailsButtons';
import ComplaintDetailsField from './components/ComplaintDetailsField';
import ComplaintErrorMessage from './components/ComplaintErrorMessage';

const FORM_SELECT_STYLE = {
	'& .MuiTypography-root': {
		display: 'none',
	},
	'& .MuiInputLabel-root': {
		color: theme.palette.primary.main,
	},
};

const ComplaintTrackingDetail = ({
	isComplaintDetails,
}: {
	isComplaintDetails: boolean;
}) => {
	const { complaintId, claimUUID } = useParams<{
		complaintId: string;
		claimUUID: string;
	}>();

	const dispatch = useDispatch<AppDispatch>();
	const { t: tClientLabels } = useTranslation('', {
		keyPrefix: TRANSLATION_KEYS.CLIENT_LABELS,
	});
	const { t: tDbLabels } = useTranslation('', {
		keyPrefix: TRANSLATION_KEYS.DB_LABELS,
	});

	const {
		complaintReasons,
		complaintOutcomes,
		complaintDetail,
		triggerRefresh,
	} = useSelector((store: RootState) => store.complaintTracking);

	const { complaintStatuses, complaintTypes, complaintSources } = useSelector(
		(store: RootState) => store.form,
	);

	const { claimData: claim } = useSelector((store: RootState) => store.claim);

	const navigate = useNavigate();

	const [isEditing, setIsEditing] = useState(!isComplaintDetails);

	const {
		errors,
		validateForm,
		isClosureDateMandatory,
		isFRLsentMandatory,
		isFRLSentDateMandatory,
		formatFRLSent,
	} = useComplaintHook();

	useEffect(() => {
		if (complaintId) {
			dispatch(getComplaintTrackingDetail({ id: complaintId }));
			setIsEditing(false);
		} else if (triggerRefresh && complaintDetail?.complaintId) {
			dispatch(
				getComplaintTrackingDetail({
					id: String(complaintDetail.complaintId),
				}),
			);
			setIsEditing(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, complaintId, triggerRefresh]);

	useEffect(() => {
		if (!complaintId && claimUUID) {
			dispatch(resetComplaintDetail(claimUUID));
		}
		return () => {
			dispatch(resetComplaintDetail(''));
		};
	}, [dispatch, complaintId, claimUUID]);

	useEffect(() => {
		if (claimUUID) {
			dispatch(getClaimDetails(claimUUID));
		} else if (complaintDetail?.claimUUID) {
			dispatch(getClaimDetails(complaintDetail?.claimUUID));
		}
	}, [dispatch, claimUUID, complaintDetail?.claimUUID]);

	const handleValueChange = (
		inputName: keyof IComplaintTrackingDetail,
		payload: string | boolean | undefined,
	) => {
		if (
			inputName === 'internalNotes' &&
			typeof payload === 'string' &&
			payload?.length > COMPLAINT_NOTE_MAX_LENGTH
		) {
			return;
		}

		if (
			inputName === 'closureDate' &&
			typeof payload === 'string' &&
			complaintDetail?.frlSentDate &&
			dayjs(payload).isAfter(dayjs(complaintDetail.frlSentDate))
		) {
			dispatch(
				setComplaintValue({
					name: 'frlSentDate',
					value: undefined,
				}),
			);
		}

		if (
			inputName === 'requestedDate' &&
			typeof payload === 'string' &&
			complaintDetail?.closureDate &&
			dayjs(payload).isAfter(dayjs(complaintDetail.closureDate))
		) {
			dispatch(
				setComplaintValue({
					name: 'closureDate',
					value: undefined,
				}),
			);
		}

		dispatch(
			setComplaintValue({
				name: inputName,
				value: payload,
			}),
		);
	};

	const handleDiscardChanges = () => {
		if (complaintDetail?.complaintId) {
			dispatch(
				getComplaintTrackingDetail({
					id: String(complaintDetail.complaintId),
				}),
			);
		}
		setIsEditing(false);
	};

	const handleNavigateToClaim = () => {
		const isFormValid = validateForm();
		if (isFormValid) {
			navigate(`/view-claims/${complaintDetail?.claimUUID}`);
		}
	};

	const handleSaveComplaint = () => {
		const isValid = validateForm();
		if (isValid) {
			dispatch(saveComplaint('')).then(() => {
				if (isComplaintDetails) {
					setIsEditing(false);
				} else {
					navigate('/view-complaint-tracking');
				}
			});
		}
	};

	return (
		<Stack gap={5} height={'100%'} direction={'column'} position={'relative'}>
			<Stack flexDirection={'row'} justifyContent={'flex-end'}>
				{isComplaintDetails && (
					<Button
						startIcon={<ShowClaim />}
						variant="text"
						onClick={handleNavigateToClaim}
						sx={{
							mr: 0,
							width: 'fit-content',
							p: 0,
							textDecoration: 'underline',
							gap: '8px',
							'&:hover': {
								textDecoration: 'underline',
							},
						}}
					>
						{tClientLabels('complaint.viewClaim')}
					</Button>
				)}
			</Stack>
			<Stack gap={7.5}>
				{!isEditing ? (
					<>
						<Stack gap={2} direction={'row'}>
							<Stack width={'100%'}>
								<ComplaintDetailsField
									label={tClientLabels('forms.header.complaintSource')}
									value={tDbLabels(
										complaintSources.find(c => c.code === complaintDetail?.source)
											?.label || '-',
									)}
									required
								/>
							</Stack>
							<Stack width={'100%'}>
								<ComplaintDetailsField
									label={tClientLabels('forms.header.complaintType')}
									value={tDbLabels(
										complaintTypes.find(c => c.code === complaintDetail?.type)?.label ||
											'-',
									)}
									required
								/>
							</Stack>
							<ComplaintDetailsField
								label={tClientLabels('forms.header.complaintNotificationDate')}
								value={
									complaintDetail?.requestedDate
										? getShortDate(complaintDetail?.requestedDate)
										: '-'
								}
								required
							/>
							<Stack width={'100%'}>
								<ComplaintDetailsField
									label={tClientLabels('forms.header.complaintStatus')}
									value={tDbLabels(
										complaintStatuses.find(c => c.code === complaintDetail?.status)
											?.label || '-',
									)}
								/>
							</Stack>
						</Stack>
						<Stack gap={2} direction={'row'}>
							<Stack width={'100%'}>
								<ComplaintDetailsField
									label={tClientLabels('forms.header.complaintReason')}
									value={tDbLabels(
										complaintReasons.find(c => c.code === complaintDetail?.reason)
											?.label || '-',
									)}
								/>
							</Stack>
							<Stack width={'100%'}>
								<ComplaintDetailsField
									label={tClientLabels('forms.header.complaintClosureDate')}
									value={
										complaintDetail?.closureDate
											? getShortDate(complaintDetail?.closureDate)
											: '-'
									}
								/>
							</Stack>
							<ComplaintDetailsField
								label={tClientLabels('forms.header.complaintOutcome')}
								value={tDbLabels(
									complaintOutcomes.find(c => c.code === complaintDetail?.outcome)
										?.label || '-',
								)}
							/>
							<Stack width={'100%'}>
								<ComplaintDetailsField
									label={tClientLabels('forms.header.FRLSent')}
									value={formatFRLSent(!!Number(complaintDetail?.frlSent))}
								/>
							</Stack>
						</Stack>
						<Stack gap={2} direction={'row'}>
							<Stack gap={2} direction={'row'} sx={{ width: '100%' }}>
								<Stack width={'100%'}>
									<ComplaintDetailsField
										label={tClientLabels('forms.header.FRLSentDate')}
										value={
											complaintDetail?.frlSentDate
												? getShortDate(complaintDetail?.frlSentDate)
												: '-'
										}
									/>
								</Stack>
								<Stack width={'100%'}>
									<ComplaintDetailsField
										label={tClientLabels('forms.header.complaintReferralToInsurerDate')}
										value={
											complaintDetail?.referralToInsurerDate
												? getShortDate(complaintDetail?.referralToInsurerDate)
												: '-'
										}
									/>
								</Stack>
							</Stack>
							<Stack width={'100%'}>
								<ComplaintDetailsField
									label={tClientLabels('forms.header.internalNotes')}
									value={complaintDetail?.internalNotes || '-'}
								/>
							</Stack>
						</Stack>
						<Stack gap={2} direction={'row'}>
							<ComplaintAttachmentsDetails />
						</Stack>
					</>
				) : (
					<>
						<Stack gap={2} direction={'row'}>
							<Stack width={'100%'} position={'relative'}>
								<SelectGeneric
									action={payload => handleValueChange('source', payload)}
									items={complaintSources.map((el, index) => {
										return {
											value: tDbLabels(el.code),
											text: tDbLabels(el.label),
											id: index,
										};
									})}
									name={'complaintSource'}
									fullWidth
									sxForm={FORM_SELECT_STYLE}
									initialValue={
										complaintDetail?.source
											? tDbLabels(complaintDetail?.source)
											: undefined
									}
									error={errors.includes('source')}
									required
								/>
								{errors.includes('source') && <ComplaintErrorMessage />}
							</Stack>
							<Stack width={'100%'} position={'relative'}>
								<SelectGeneric
									disabled //this only changes with a job
									action={payload => handleValueChange('type', payload)}
									items={complaintTypes.map((el, index) => {
										return {
											value: tDbLabels(el.code),
											text: tDbLabels(el.label),
											id: index,
										};
									})}
									name={'complaintType'}
									fullWidth
									sxForm={FORM_SELECT_STYLE}
									initialValue={
										complaintDetail?.type ? tDbLabels(complaintDetail?.type) : undefined
									}
									error={errors.includes('type')}
									required
								/>
								{errors.includes('type') && <ComplaintErrorMessage />}
							</Stack>
							<Stack
								width={'100%'}
								position={'relative'}
								sx={
									errors.includes('requestedDate')
										? {
												'& .MuiInputBase-root': {
													borderColor: theme => theme.palette.error.main,
												},
											}
										: {}
								}
							>
								<DatePicker
									readonly
									minDate={claim?.claimDate ? dayjs(claim?.claimDate) : undefined}
									maxDate={dayjs()}
									name={'requestedDate'}
									variant="field"
									label={tClientLabels('forms.header.complaintNotificationDate')}
									value={
										complaintDetail?.requestedDate
											? dayjs(complaintDetail?.requestedDate)
											: null
									}
									onChange={value =>
										handleValueChange('requestedDate', formatDayjsForBE(value))
									}
									required
								/>
								{errors.includes('requestedDate') && <ComplaintErrorMessage />}
							</Stack>
							<Stack width={'100%'} position={'relative'}>
								<SelectGeneric
									action={payload => handleValueChange('status', payload)}
									items={complaintStatuses.map((el, index) => {
										return {
											value: tDbLabels(el.code),
											text: tDbLabels(el.label),
											id: index,
										};
									})}
									name={'complaintStatus'}
									fullWidth
									sxForm={FORM_SELECT_STYLE}
									initialValue={
										complaintDetail?.status
											? tDbLabels(complaintDetail?.status)
											: undefined
									}
									error={errors.includes('status')}
									required
								/>
								{errors.includes('status') && <ComplaintErrorMessage />}
							</Stack>
						</Stack>
						<Stack gap={2} direction={'row'}>
							<Stack width={'100%'}>
								<SelectGeneric
									action={payload => handleValueChange('reason', payload)}
									items={complaintReasons.map((el, index) => {
										return {
											value: tDbLabels(el.code),
											text: tDbLabels(el.label),
											id: index,
										};
									})}
									name={'complaintReason'}
									fullWidth
									initialValue={
										complaintDetail?.reason
											? tDbLabels(complaintDetail?.reason)
											: undefined
									}
								/>
							</Stack>
							<Stack
								width={'100%'}
								position={'relative'}
								sx={
									errors.includes('closureDate')
										? {
												'& .MuiInputBase-root': {
													borderColor: theme => theme.palette.error.main,
												},
											}
										: {}
								}
							>
								<DatePicker
									readonly
									minDate={
										complaintDetail?.requestedDate
											? dayjs(complaintDetail?.requestedDate)
											: undefined
									}
									name={'closureDate'}
									variant="field"
									label={tClientLabels('forms.header.complaintClosureDate')}
									value={
										complaintDetail?.closureDate
											? dayjs(complaintDetail?.closureDate)
											: null
									}
									onChange={value =>
										handleValueChange('closureDate', formatDayjsForBE(value))
									}
									required={isClosureDateMandatory}
								/>
								{errors.includes('closureDate') && <ComplaintErrorMessage />}
							</Stack>
							<Stack width={'100%'}>
								<SelectGeneric
									action={payload => handleValueChange('outcome', payload)}
									items={complaintOutcomes.map((el, index) => {
										return {
											value: tDbLabels(el.code),
											text: tDbLabels(el.label),
											id: index,
										};
									})}
									name={'outcome'}
									fullWidth
									initialValue={
										complaintDetail?.outcome
											? tDbLabels(complaintDetail?.outcome)
											: undefined
									}
								/>
							</Stack>
							<Stack width={'100%'} position={'relative'}>
								<SelectGeneric
									action={payload =>
										handleValueChange(
											'frlSent',
											payload === 'true' ? true : payload === 'false' ? false : undefined,
										)
									}
									items={[
										{ value: 'true', text: tDbLabels('complaint_FRLSent.yes') },
										{ value: 'false', text: tDbLabels('complaint_FRLSent.no') },
									].map((el, index) => {
										return {
											value: el.value,
											text: el.text,
											id: index,
										};
									})}
									name={'frlSent'}
									fullWidth
									sxForm={FORM_SELECT_STYLE}
									initialValue={
										complaintDetail?.frlSent !== undefined &&
										complaintDetail?.frlSent !== null
											? Number(complaintDetail.frlSent)
												? 'true'
												: 'false'
											: undefined
									}
									error={errors.includes('inputFRLSent')}
									required={isFRLsentMandatory}
								/>
								{errors.includes('frlSent') && <ComplaintErrorMessage />}
							</Stack>
						</Stack>
						<Stack gap={2} direction={'row'}>
							<Stack gap={2} direction={'row'} sx={{ width: '100%' }}>
								<Stack
									width={'100%'}
									position={'relative'}
									sx={
										errors.includes('frlSentDate')
											? {
													'& .MuiInputBase-root': {
														borderColor: theme => theme.palette.error.main,
													},
												}
											: {}
									}
								>
									<DatePicker
										readonly
										minDate={
											complaintDetail?.closureDate
												? dayjs(complaintDetail?.closureDate)
												: undefined
										}
										name={'frlSentDate'}
										variant="field"
										label={tClientLabels('forms.header.FRLSentDate')}
										value={
											complaintDetail?.frlSentDate
												? dayjs(complaintDetail?.frlSentDate)
												: null
										}
										onChange={value =>
											handleValueChange('frlSentDate', formatDayjsForBE(value))
										}
										required={isFRLSentDateMandatory}
									/>
									{errors.includes('frlSentDate') && <ComplaintErrorMessage />}
								</Stack>
								<DatePicker
									readonly
									minDate={
										complaintDetail?.frlSentDate
											? dayjs(complaintDetail?.frlSentDate)
											: undefined
									}
									name={'referralToInsurerDate'}
									variant="field"
									label={tClientLabels('forms.header.complaintReferralToInsurerDate')}
									value={
										complaintDetail?.referralToInsurerDate
											? dayjs(complaintDetail?.referralToInsurerDate)
											: null
									}
									onChange={value =>
										handleValueChange('referralToInsurerDate', formatDayjsForBE(value))
									}
								/>
							</Stack>
							<Stack width={'100%'} position={'relative'}>
								<TextField
									label={tClientLabels('forms.header.internalNotes')}
									sx={{ width: '100%' }}
									value={complaintDetail?.internalNotes}
									onChange={event =>
										handleValueChange('internalNotes', event.target.value)
									}
								/>
								<Typography
									variant="caption"
									color={theme => theme.palette.grey1.main}
									position={'absolute'}
									bottom={-22}
									left={12}
								>
									{tClientLabels('complaint.internalNotes.maxLength', {
										noteLength: complaintDetail?.internalNotes.length,
										maxLength: COMPLAINT_NOTE_MAX_LENGTH,
									})}
								</Typography>
							</Stack>
						</Stack>
						<UploadGrid
							documentType="complaint"
							data={complaintDetail?.attachments}
							clearDocumentsKey={STATE_KEYS.COMPLAINT}
						/>
					</>
				)}
			</Stack>
			<ComplaintDetailsButtons
				isEditing={isEditing}
				setIsEditing={setIsEditing}
				isComplaintDetails={isComplaintDetails}
				handleNavigateBack={handleNavigateToClaim}
				handleSaveComplaint={handleSaveComplaint}
				handleDiscardChanges={handleDiscardChanges}
			/>
		</Stack>
	);
};

export default ComplaintTrackingDetail;
